import React, { forwardRef } from 'react';
import { Monitor, Moon, MoreHorizontal, Settings, Sun, Trash2 } from 'lucide-react';
import { Link } from './ui/link';
import { ButtonTooltip, TooltipProvider } from './ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { ThemeToggle } from './ThemeToggle';
import { OfflineIndicator } from './OfflineIndicator';
import { RotatingSettingsIcon } from './RotatingSettingsIcon';
import { RotatingTrashIcon } from './RotatingTrashIcon';
import { ScalingShareIcon } from './ScalingShareIcon';
import { CONCEPT_ICONS } from '../utils/concept-icons';
import { useLocale } from '../contexts/LocaleContext';
import { useTheme } from '../contexts/ThemeContext';
import { cn } from '../lib/utils';
import {
  SIDEBAR_ACTION_GROUP_CLASSNAME,
  SIDEBAR_ACTION_HOVER_GROUP,
} from '../utils/sidebar-action-hover';
import type { SidebarActionsMode } from '../hooks/use-sidebar-actions-fit.js';

interface FixedIconLinkProps {
  to: string;
  label: string;
  icon: React.ReactNode;
  onClick?: () => void;
  isActive?: boolean;
}

const FixedIconLink: React.FC<FixedIconLinkProps> = ({ to, label, icon, onClick, isActive }) => (
  <ButtonTooltip tooltip={label} side="top">
    <Link
      to={to}
      onClick={onClick}
      aria-label={label}
      data-active={isActive}
      className={cn(
        SIDEBAR_ACTION_GROUP_CLASSNAME,
        'flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-secondary-foreground transition-colors hover:bg-sidebar-accent hover:text-[var(--scribane-green)]',
      )}
    >
      {icon}
    </Link>
  </ButtonTooltip>
);

export interface ProjectSidebarActionsProps {
  mode: SidebarActionsMode;
  isCollapsed: boolean;
  projectId: string;
  canEditProject: boolean;
  canManageSharing: boolean;
  isPathActive: (path: string) => boolean;
  onNavigate?: () => void;
}

export const ProjectSidebarActions = forwardRef<HTMLDivElement, ProjectSidebarActionsProps>(
  function ProjectSidebarActions(
    {
      mode,
      isCollapsed,
      projectId,
      canEditProject,
      canManageSharing,
      isPathActive,
      onNavigate,
    },
    ref,
  ) {
    const { t } = useLocale();
    const { setTheme } = useTheme();
    const ShareIcon = CONCEPT_ICONS.share;
    const settingsPath = `/projects/${projectId}/settings`;
    const sharingPath = `/projects/${projectId}/sharing`;
    const trashPath = `/projects/${projectId}/trash`;

    const actionBarClassName = cn(
      'flex shrink-0 items-center gap-1 px-2 py-2',
      isCollapsed ? 'flex-col' : 'flex-row justify-start',
      '[&_[data-active=true]]:!bg-[rgba(94,204,128,0.16)] [&_[data-active=true]]:!text-primary [&_[data-active=true]]:hover:!bg-[rgba(94,204,128,0.16)] [&_[data-active=true]]:hover:!text-primary [&_[data-active=true]_svg]:!text-[var(--scribane-green)]',
      '[&_[data-state=open]]:!bg-[rgba(94,204,128,0.16)] [&_[data-state=open]]:!text-primary [&_[data-state=open]_svg]:!text-[var(--scribane-green)]',
    );

    if (mode === 'compact' && isCollapsed) {
      return (
        <TooltipProvider delayDuration={0}>
          <div ref={ref} className={actionBarClassName}>
            <DropdownMenu>
              <ButtonTooltip tooltip={t('sidebar_more_actions')} side="top">
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label={t('sidebar_more_actions')}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-secondary-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                  >
                    <MoreHorizontal className="!h-4 !w-4" />
                  </button>
                </DropdownMenuTrigger>
              </ButtonTooltip>
              <DropdownMenuContent align="start" side="right" collisionPadding={8}>
                <DropdownMenuItem onClick={() => setTheme('light')}>
                  <Sun className="h-4 w-4" />
                  {t('theme_light')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme('dark')}>
                  <Moon className="h-4 w-4" />
                  {t('theme_dark')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme('system')}>
                  <Monitor className="h-4 w-4" />
                  {t('theme_system')}
                </DropdownMenuItem>
                {(canEditProject || canManageSharing) && <DropdownMenuSeparator />}
                {canEditProject && (
                  <DropdownMenuItem asChild>
                    <Link to={settingsPath} onClick={onNavigate}>
                      <Settings className="h-4 w-4" />
                      {t('project_settings')}
                    </Link>
                  </DropdownMenuItem>
                )}
                {canManageSharing && (
                  <DropdownMenuItem asChild>
                    <Link to={sharingPath} onClick={onNavigate}>
                      <ShareIcon className="h-4 w-4" />
                      {t('share_project')}
                    </Link>
                  </DropdownMenuItem>
                )}
                {canEditProject && (
                  <DropdownMenuItem asChild>
                    <Link to={trashPath} onClick={onNavigate}>
                      <Trash2 className="h-4 w-4" />
                      {t('trash')}
                    </Link>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <OfflineIndicator variant="sidebar" isCollapsed />
          </div>
        </TooltipProvider>
      );
    }

    return (
      <TooltipProvider delayDuration={0}>
        <div ref={ref} className={actionBarClassName}>
          <ThemeToggle
            collapsed={isCollapsed}
            className={isCollapsed ? '!h-9 !w-9 !p-0' : undefined}
            iconClassName={isCollapsed ? '!h-4 !w-4 text-secondary-foreground' : undefined}
            tooltipSide="top"
            menuSide="top"
          />
          {canEditProject && (
            <FixedIconLink
              to={settingsPath}
              label={t('project_settings')}
              icon={<RotatingSettingsIcon className="!h-4 !w-4" />}
              onClick={onNavigate}
              isActive={isPathActive(settingsPath)}
            />
          )}
          {canManageSharing && (
            <FixedIconLink
              to={sharingPath}
              label={t('share_project')}
              icon={<ScalingShareIcon className="!h-4 !w-4" />}
              onClick={onNavigate}
              isActive={isPathActive(sharingPath)}
            />
          )}
          {canEditProject && (
            <FixedIconLink
              to={trashPath}
              label={t('trash')}
              icon={<RotatingTrashIcon className="!h-4 !w-4" hoverGroup={SIDEBAR_ACTION_HOVER_GROUP} />}
              onClick={onNavigate}
              isActive={isPathActive(trashPath)}
            />
          )}
          <OfflineIndicator variant="sidebar" isCollapsed={isCollapsed} />
        </div>
      </TooltipProvider>
    );
  },
);
